import { get } from 'core/libs/lodash';

import { removeDuplicateSlashes } from 'core/utils/url-helper';
import { resolve } from 'core/utils/env';

/**
 * Усредненная ширина контентной области десктопной версии при максимальной
 * ширине сайта в 1280px и боковой колонкой размером в 300px.
 */
const DEFAULT_WIDTH = 750;

/**
 * Определяет минимальное значение для ширины и высоты, учитывая, что
 * что-то может быть не указано.
 */
export function getMinValue(value, maxValue) {
  return value && maxValue ? Math.min(value, maxValue) : value || maxValue;
}

/**
 * Используется в компоненте для получения исходного пути к изображению или видео в зависимости от текущего брейкпоинта и выбранной версии.
 */
export function getSrc(props) {
  const {
    src,
    breakpoint,
    versions,
    version,
  } = props;

  if (src) return src;

  const mobileVideoSrc = breakpoint === 'mobile'
    && version === 'original'
    && versions?.original?.type === 'video'
    && versions?.mobile_video;

  if (mobileVideoSrc) return mobileVideoSrc;

  return versions?.[version];
}

/**
 * Вычисляет размеры для картинки, учитывая соотношения сторон.
 * Для того что-бы меньше нагружать резалку картинок, для большинства
 * случаев мы не режем картинку, а изменяем ее размер, и далее средствами
 * css встраиваем ее в контейнер с нужными размерами.
 *
 * TODO: Тесты для это утилиты отдельно не написаны, но ее функционал
 * тестируется в компоненте SmartImage и generateThumborBaseUri
 * Вожможно стоит вынести эти тесты отдельно.
 */
export function getThumbSize(params) {
  const {
    src,
    width,
    height,
    maxWidth,
    maxHeight,
    aspectRatio,
    cropWithRatio,
    dpr,
  } = params;

  const imageWidth = getMinValue(width, maxWidth) || 0;
  const imageHeight = getMinValue(height, maxHeight) || 0;

  const ceiledDpr = Math.ceil(dpr || 1);

  const srcRatio = src.width / src.height;
  const containerRatio = aspectRatio || (imageWidth / height) || srcRatio;

  let thumbWidth = imageWidth || (imageHeight * containerRatio) || DEFAULT_WIDTH;
  let thumbHeight = imageHeight || (thumbWidth / containerRatio);
  thumbWidth = Math.ceil(thumbWidth * ceiledDpr);
  thumbHeight = Math.ceil(thumbHeight * ceiledDpr);

  if (srcRatio > containerRatio) {
    const cropWidth = cropWithRatio && Math.floor(thumbHeight * containerRatio);
    return {
      width: cropWidth || 0,
      height: thumbHeight,
    };
  }

  const cropHeight = cropWithRatio && Math.floor(thumbWidth / containerRatio);
  return {
    width: thumbWidth,
    height: cropHeight || 0,
  };
}

/**
 * Собирает необходимые параметры для thumbor
 *
 * Подробнее про порядок параметров можно узнать тут:
 * https://github.com/thumbor/thumbor/wiki/Usage#image-endpoint
 *
 * Важно понимать особенность работы сервиса умной резалки и параметра smart.
 * Параметр smart включает умную нарезку.
 * Без указания /smart/ в пути, нода выбирается случайно из нод imgproxy и thumbor.
 * Если нам нужно сделать апскейл картинки, то:
 *   - imgproxy увеличивать катинки не умеет
 *   - thumbor увеличивать умеет
 *
 * Поэтому такой запрос за картинкой ничего не увеличит:
 * https://quto.ru/thumb/1280x720/imgs/2022/07/27/10/5513720/124d23d88ad8c081df7175047ff5865a8194850a.webp
 * (точнее если попадет на ноду c thumbor то могу бы и увеличить, но не хватает filters)
 *
 * А при передаче /smart/SOME_FILTERS/ увеличится:
 * https://quto.ru/thumb/1280x720/smart/filters:quality(75)/imgs/2022/07/27/10/5513720/124d23d88ad8c081df7175047ff5865a8194850a.webp
 *
 * Подробнее про то как работать с апскейлом картинок, можно посмотреть в комментарии к задаче UCMS-8943
 * или конкретный комментарий:
 * https://jira.rambler-co.ru/browse/UCMS-8943#comment-4548788
 */
export function generateThumborBaseUri(params) {
  const {
    src,
    width,
    height,
    maxWidth,
    maxHeight,
    aspectRatio,
    dpr,
    fastBlur,
    cropWithRatio,
    withoutQualityOptimize,
    smart,
  } = params;

  const thumbSize = getThumbSize({
    src,
    width,
    height,
    maxWidth,
    maxHeight,
    aspectRatio,
    dpr,
    cropWithRatio: cropWithRatio || smart,
  });

  const filters = [];
  const uriComponents = [`${thumbSize.width}x${thumbSize.height}`];

  if (smart) uriComponents.push('smart');

  /**
   * Оптимизация работает только для JPG:
   * https://github.com/thumbor/thumbor/wiki/Quality#description
   * Решено было ее отключить, потому что она потребляет слишком много ресурсов
   */
  if (!withoutQualityOptimize) filters.push('quality(75)');
  if (fastBlur) filters.push('blur(7)');
  /**
   * Указываем фильтр `no_upscale` для контроля над увеличением картинок для ретина экранов
   * НЕ актуально для `previewUrl`
   */
  if (dpr) filters.push('no_upscale()');

  if (filters.length) {
    uriComponents.push(['filters'].concat(filters).join(':'));
  }

  return uriComponents.join('/');
}

/**
 * Собирает ссылку на изображение
 */
export default function getImageUrl(params) {
  const {
    src,
    url,
    width,
    height,
    maxWidth,
    maxHeight,
    aspectRatio,
    webdavHost,
    thumborHost,
    dpr,
    fastBlur,
    cropWithRatio,
    withoutQualityOptimize,
    smart,
  } = params;

  const hasSrcRelUrl = !!(src && src.rel_url);
  const canUseThumborSrc = !!(thumborHost && hasSrcRelUrl);

  let imageUrl = hasSrcRelUrl ? src.rel_url : (url || src.url);

  /**
   * На DEV машине не резолвим `assest` через thumbr (!isLocalAsset)
   */
  const isLocalAsset = resolve({
    '*': false,
    'dev': /^\/assets\//.test(imageUrl),
  });

  if (hasSrcRelUrl && !isLocalAsset) {
    imageUrl = (webdavHost || '') + imageUrl;
  }

  if (canUseThumborSrc && !isLocalAsset) {
    const imageWebdavUrl = removeDuplicateSlashes(imageUrl);
    imageUrl = [
      thumborHost,
      generateThumborBaseUri({
        src,
        width,
        height,
        maxWidth,
        maxHeight,
        aspectRatio,
        dpr,
        fastBlur,
        cropWithRatio,
        withoutQualityOptimize,
        smart,
      }),
      imageWebdavUrl,
    ].join('/');
  }

  return imageUrl;
}

/**
 * Фомирует картинку для og:image и schema.org
 * @param {Object} params
 * @param {object} params.primary - Первичная переобложка. Если указана, то выводится она без ресайза
 * @param {object} params.secondary - Вторичная переобложка. Если указана, то ресайзится до 1280x720
 * @param {string} params.webdavHost
 * @param {string} params.thumborHost
 * @param {bool} params.smart Использовать ли умную нарезку в thumbor
 * @param {number} params.width ширина ресайза
 * @param {number} params.height высота ресайза
 */
export function getMetaCover(params) {
  const {
    primary,
    secondary,
    webdavHost,
    thumborHost,
    smart,
    width = 1280,
    height = 720,
  } = params;

  if (primary && primary.size) {
    return {
      ...primary,
      rel_url: getImageUrl({ src: primary, webdavHost }),
    };
  }

  if (!(secondary && secondary.size)) return null;

  const resizeParams = {
    src: secondary,
    width,
    height,
    aspectRatio: width / height,
    cropWithRatio: true,
    smart,
  };

  const secondaryUrl = getImageUrl({
    ...resizeParams,
    webdavHost,
    thumborHost,
  });

  const thumbSize = getThumbSize(resizeParams);

  return {
    ...secondary,
    ...thumbSize,
    rel_url: secondaryUrl,
  };
}

/**
 * Проверяет на наличие save-data
 * @param {bool}
 */
export function isSaveData() {
  return get(process.env.BROWSER_RUNTIME ? navigator : {}, 'connection.saveData');
}
